
import { useState, useEffect } from 'react';
import { supabase, type Game } from '../../../lib/supabase';
import { teamsData, getRivalTeams, getOwnTeams } from '../../../data/teams';
import AddGameModal from './AddGameModal';

const GamesList = () => {
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingGame, setEditingGame] = useState<Game | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    home_team: '',
    away_team: '',
    date: '',
    time: '',
    location: '',
    home_team_logo: '',
    away_team_logo: '',
    jornada: ''
  });

  const ownTeams = getOwnTeams();
  const rivalTeams = getRivalTeams();

  useEffect(() => {
    fetchGames();
  }, [refreshTrigger]);

  const fetchGames = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('Obteniendo partidos de la base de datos...');
      
      const { data, error } = await supabase
        .from('games')
        .select('*')
        .order('date', { ascending: false }); // Cambiado a false para mostrar más recientes primero
      
      if (error) {
        console.error('Error al obtener partidos:', error);
        throw new Error(`Error de base de datos: ${error.message}`);
      }
      
      console.log('Partidos obtenidos:', data);
      setGames(data || []);
    } catch (error: any) {
      console.error('Error fetching games:', error);
      setError(error.message || 'Error al cargar los partidos');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteGame = async (id: string) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este partido?')) {
      return;
    }

    try {
      console.log('Eliminando partido con ID:', id);
      
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar partido:', error);
        throw new Error(`Error de base de datos: ${error.message}`);
      }
      
      console.log('Partido eliminado exitosamente');
      setGames(games.filter(game => game.id !== id));
    } catch (error: any) {
      console.error('Error deleting game:', error);
      alert(`Error al eliminar el partido: ${error.message}`);
    }
  };

  const handleEditGame = (game: Game) => {
    setEditingGame(game);
    setEditForm({
      home_team: game.home_team || '',
      away_team: game.away_team || '',
      date: game.date,
      time: game.time,
      location: game.location || '',
      home_team_logo: game.home_team_logo || '',
      away_team_logo: game.away_team_logo || '',
      jornada: game.jornada?.toString() || ''
    });
    setShowEditModal(true);
    setError(null);
  };

  const handleUpdateGame = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingGame) return;

    try {
      setError(null);
      
      // Validar que los equipos no sean iguales
      if (editForm.home_team === editForm.away_team) {
        throw new Error('El equipo local y visitante no pueden ser el mismo');
      }

      const updateData = {
        home_team: editForm.home_team.trim(),
        away_team: editForm.away_team.trim(),
        date: editForm.date,
        time: editForm.time,
        location: editForm.location.trim() || null,
        home_team_logo: editForm.home_team_logo.trim() || null,
        away_team_logo: editForm.away_team_logo.trim() || null,
        jornada: editForm.jornada ? parseInt(editForm.jornada) : null
      };

      console.log('Actualizando partido:', updateData);

      const { error } = await supabase
        .from('games')
        .update(updateData)
        .eq('id', editingGame.id);

      if (error) {
        console.error('Error al actualizar partido:', error);
        throw new Error(`Error de base de datos: ${error.message}`);
      }

      console.log('Partido actualizado exitosamente');

      setGames(games.map(game => 
        game.id === editingGame.id 
          ? { ...game, ...updateData }
          : game
      ));

      setShowEditModal(false);
      setEditingGame(null);
    } catch (error: any) {
      console.error('Error updating game:', error);
      setError(error.message || 'Error al actualizar el partido');
    }
  };

  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setEditForm({
      ...editForm,
      [e.target.name]: e.target.value
    });
    // Limpiar error cuando el usuario empiece a escribir
    if (error) setError(null);
  };

  const openEditMediaSelector = (field: 'home_team_logo' | 'away_team_logo') => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          alert('El archivo es demasiado grande. Máximo 5MB.');
          return;
        }

        if (!file.type.startsWith('image/')) {
          alert('Por favor selecciona un archivo de imagen válido.');
          return;
        }

        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const result = e.target?.result as string;
            setEditForm(prev => ({ ...prev, [field]: result }));
          };
          reader.onerror = () => {
            alert('Error al leer el archivo. Por favor intenta de nuevo.');
          };
          reader.readAsDataURL(file);
        } catch (error) {
          console.error('Error al procesar imagen:', error);
          alert('Error al procesar la imagen');
        }
      }
    };
    input.click();
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto"></div>
        <p className="text-gray-400 mt-2">Cargando partidos...</p>
      </div>
    );
  }

  if (error && games.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-4">
          <i className="ri-error-warning-line text-2xl text-red-400 mb-2"></i>
          <p className="text-red-400 text-sm">{error}</p>
          <button
            onClick={fetchGames}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {games.length === 0 ? (
        <div className="text-center py-8">
          <i className="ri-calendar-line text-4xl text-gray-5

0 mb-2"></i>
          <p className="text-gray-400">No hay partidos programados</p>
        </div>
      ) : (
        <div className="space-y-3">
          {games.map((game) => (
            <div key={game.id} className="bg-gray-700 border border-gray-600 rounded-lg p-4">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-white text-sm sm:text-base truncate">
                    {game.home_team} vs {game.away_team}
                  </h4>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 mt-2 text-xs sm:text-sm text-gray-300">
                    {game.jornada && (
                      <span className="flex items-center">
                        <i className="ri-trophy-line mr-1 flex-shrink-0"></i>
                        Jornada {game.jornada}
                      </span>
                    )}
                    <span className="flex items-center">
                      <i className="ri-calendar-line mr-1 flex-shrink-0"></i>
                      {new Date(game.date).toLocaleDateString('es-ES')}
                    </span>
                    <span className="flex items-center">
                      <i className="ri-time-line mr-1 flex-shrink-0"></i>
                      {game.time}
                    </span>
                    {game.location && (
                      <span className="flex items-center min-w-0">
                        <i className="ri-map-pin-line mr-1 flex-shrink-0"></i>
                        <span className="truncate">{game.location}</span>
                      </span>
                    )}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <span className="inline-block bg-blue-600 text-blue-101 text-xs px-2 py-1 rounded-full">
                      Local: {game.home_team}
                    </span>
                    <span className="inline-block bg-green-600 text-green-101 text-xs px-2 py-1 rounded-full">
                      Visitante: {game.away_team}
                    </span>
                  </div>
                </div>
                
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleEditGame(game)}
                    className="p-2 text-blue-400 hover:bg-gray-600 rounded-lg transition-colors"
                    title="Editar partido"
                  >
                    <i className="ri-edit-line"></i>
                  </button>
                  <button
                    onClick={() => deleteGame(game.id)}
                    className="p-2 text-red-400 hover:bg-gray-600 rounded-lg transition-colors"
                    title="Eliminar partido"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de edición */}
      {showEditModal && editingGame && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Editar Partido</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    <span className="text-sm">{error}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleUpdateGame} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Jornada
                  </label>
                  <select
                    name="jornada"
                    value={editForm.jornada}
                    onChange={handleEditFormChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar jornada</option>
                    {Array.from({ length: 20 }, (_, i) => i + 1).map(num => (
                      <option key={num} value={num}>
                        Jornada {num}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipo Local
                  </label>
                  <select
                    name="home_team"
                    value={editForm.home_team}
                    onChange={handleEditFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar equipo local</option>
                    <optgroup label="Nuestros Equipos">
                      {ownTeams.map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Liga Insular - Grup 3">
                      {rivalTeams.filter(team => team.category === 'Insular' && team.group === 'Grup-3').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Liga Insular - Grup 4">
                      {rivalTeams.filter(team => team.category === 'Insular' && team.group === 'Grup-4').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Categoría Mini">
                      {rivalTeams.filter(team => team.category === 'Mini').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Categoría Premini">
                      {rivalTeams.filter(team => team.category === 'Premini').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Equipo Visitante
                  </label>
                  <select
                    name="away_team"
                    value={editForm.away_team}
                    onChange={handleEditFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar equipo visitante</option>
                    <optgroup label="Nuestros Equipos">
                      {ownTeams.map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Liga Insular - Grup 3">
                      {rivalTeams.filter(team => team.category === 'Insular' && team.group === 'Grup-3').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Liga Insular - Grup 4">
                      {rivalTeams.filter(team => team.category === 'Insular' && team.group === 'Grup-4').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Categoría Mini">
                      {rivalTeams.filter(team => team.category === 'Mini').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                    <optgroup label="Categoría Premini">
                      {rivalTeams.filter(team => team.category === 'Premini').map(team => (
                        <option key={team.id} value={team.name}>{team.name}</option>
                      ))}
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo Equipo Local
                  </label>
                  <button
                    type="button"
                    onClick={() => openEditMediaSelector('home_team_logo')}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border-2 border-dashed border-gray-300 transition-colors"
                  >
                    <i className="ri-upload-line mr-2"></i>
                    {editForm.home_team_logo ? 'Cambiar Logo' : 'Seleccionar desde Medios'}
                  </button>
                  {editForm.home_team_logo && (
                    <div className="mt-2">
                      <img
                        src={editForm.home_team_logo}
                        alt="Preview"
                        className="w-16 h-16 object-contain mx-auto border border-gray-300 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, home_team_logo: '' })}
                        className="mt-2 text-sm text-red-600 hover:text-red-700 block mx-auto"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Eliminar logo
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Logo Equipo Visitante
                  </label>
                  <button
                    type="button"
                    onClick={() => openEditMediaSelector('away_team_logo')}
                    className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 border-2 border-dashed border-gray-300 transition-colors"
                  >
                    <i className="ri-upload-line mr-2"></i>
                    {editForm.away_team_logo ? 'Cambiar Logo' : 'Seleccionar desde Medios'}
                  </button>
                  {editForm.away_team_logo && (
                    <div className="mt-2">
                      <img
                        src={editForm.away_team_logo}
                        alt="Preview"
                        className="w-16 h-16 object-contain mx-auto border border-gray-300 rounded"
                      />
                      <button
                        type="button"
                        onClick={() => setEditForm({ ...editForm, away_team_logo: '' })}
                        className="mt-2 text-sm text-red-600 hover:text-red-700 block mx-auto"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>
                        Eliminar logo
                      </button>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={editForm.date}
                    onChange={handleEditFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora
                  </label>
                  <input
                    type="time"
                    name="time"
                    value={editForm.time}
                    onChange={handleEditFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ubicación
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={editForm.location}
                    onChange={handleEditFormChange}
                    placeholder="Ej: Polideportivo Municipal"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    Actualizar Partido
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GamesList;
